// src/pages/Auth/RegisterPage.js
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AuthPages.css';

import { useDocumentTitle } from '../../hooks/useDocumentTitle';
export default function RegisterPage() {
  useDocumentTitle('Créer un compte');

  const { register, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Si la visiteuse a été renvoyée ici depuis une page protégée (le tunnel de
  // commande, typiquement), on la ramène à cette page après inscription plutôt
  // que vers son espace client : elle reprend son achat là où elle l'a laissé.
  const from = location.state?.from?.pathname || '/mon-compte';

  const [form, setForm]       = useState({ nom: '', prenom: '', email: '', password: '', confirm: '' });
  const [erreur, setErreur]   = useState('');
  const [loading, setLoading] = useState(false);

  if (isLoggedIn) { navigate(from, { replace: true }); return null; }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErreur('');
  }

  function valider() {
    if (!form.nom || !form.prenom || !form.email || !form.password)
      return 'Veuillez remplir tous les champs obligatoires.';
    if (form.password.length < 8)
      return 'Le mot de passe doit contenir au moins 8 caractères.';
    if (form.password !== form.confirm)
      return 'Les mots de passe ne correspondent pas.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return 'Adresse email invalide.';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = valider();
    if (err) { setErreur(err); return; }
    setLoading(true);
    try {
      await register(form.nom, form.prenom, form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      setErreur(err.message);
    } finally {
      setLoading(false);
    }
  }

  const fields = [
    { name: 'prenom',   label: 'Prénom *',          type: 'text',     placeholder: 'Votre prénom',        half: true },
    { name: 'nom',      label: 'Nom *',              type: 'text',     placeholder: 'Votre nom',            half: true },
    { name: 'email',    label: 'Adresse email *',    type: 'email',    placeholder: 'votre@email.com',     half: false },
    { name: 'password', label: 'Mot de passe *',     type: 'password', placeholder: '8 caractères minimum', half: false },
    { name: 'confirm',  label: 'Confirmer le mot de passe *', type: 'password', placeholder: '••••••••',  half: false },
  ];

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--wide">
        <div className="auth-card__header">
          <Link to="/" className="auth-logo">
            <span className="auth-logo__top">Les Bazars</span>
            <span className="auth-logo__bottom">de Keassa</span>
          </Link>
          <h1 className="auth-card__titre">Créer un compte</h1>
          <p className="auth-card__sous">Rejoignez notre communauté et profitez d'une expérience personnalisée</p>
        </div>

        {erreur && (
          <div className="auth-erreur" role="alert">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {erreur}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="auth-row">
            {fields.filter(f => f.half).map(f => (
              <div key={f.name} className="auth-field">
                <label htmlFor={f.name} className="auth-label">{f.label}</label>
                <input id={f.name} name={f.name} type={f.type}
                  className="auth-input" placeholder={f.placeholder}
                  value={form[f.name]} onChange={handleChange} required />
              </div>
            ))}
          </div>

          {fields.filter(f => !f.half).map(f => (
            <div key={f.name} className="auth-field">
              <label htmlFor={f.name} className="auth-label">{f.label}</label>
              <input id={f.name} name={f.name} type={f.type}
                className={`auth-input${f.name === 'confirm' && form.confirm && form.confirm !== form.password ? ' auth-input--error' : ''}`}
                placeholder={f.placeholder}
                value={form[f.name]} onChange={handleChange} required />
              {f.name === 'confirm' && form.confirm && form.confirm !== form.password && (
                <p className="auth-field-hint auth-field-hint--error">Les mots de passe ne correspondent pas</p>
              )}
              {f.name === 'password' && form.password && (
                <div className="auth-strength">
                  <div className={`auth-strength__bar auth-strength__bar--${
                    form.password.length < 6 ? 'faible' :
                    form.password.length < 10 ? 'moyen' : 'fort'
                  }`} />
                  <span className="auth-strength__label">
                    {form.password.length < 6 ? 'Faible' : form.password.length < 10 ? 'Moyen' : 'Fort'}
                  </span>
                </div>
              )}
            </div>
          ))}

          <p className="auth-field-hint">* Champs obligatoires — Vos données sont protégées (RGPD)</p>

          <button type="submit"
            className={`btn-or auth-submit${loading ? ' auth-submit--loading' : ''}`}
            disabled={loading}>
            {loading ? <><span className="auth-spinner" />Création du compte...</> : 'Créer mon compte'}
          </button>
        </form>

        <p className="auth-footer-text">
          Déjà un compte ?{' '}
          <Link to="/connexion" state={{ from: location.state?.from }} className="auth-link">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}